/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  buildCodecForObject,
  Codec,
  codecForBoolean,
} from "@gnu-taler/taler-util";
import { ComponentChildren, createContext, h, VNode } from "preact";
import { useContext, useState } from "preact/hooks";
import { buildStorageKey, useLocalStorage } from "../hooks/useLocalStorage.js";

const TALER_SCREEN_ID = 102;

interface Preferences {
  showDebugInfo: boolean;
}
interface Type extends Preferences {
  toggleShowDebugInfo(): void;
}

const codecForPreferences = (): Codec<Preferences> =>
  buildCodecForObject<Preferences>()
    .allowExtra()
    .property("showDebugInfo", codecForBoolean())
    .build("CommonPreferences");

const COMMON_PREFERENCES_KEY = buildStorageKey(
  "common-preferences",
  codecForPreferences(),
);

const initial: Type = {
  showDebugInfo: true,
  toggleShowDebugInfo() {},
};

export function useCommonPreferences(): [
  Readonly<Preferences>,
  <T extends keyof Preferences>(key: T, value: Preferences[T]) => void,
] {
  const { value, update } = useLocalStorage(COMMON_PREFERENCES_KEY, initial);

  function updateField<T extends keyof Preferences>(k: T, v: Preferences[T]) {
    const newValue = { ...value, [k]: v };
    update(newValue);
  }
  return [value, updateField];
};
